import { Box } from '@mui/material';
import { colors, IconButton, Typography } from '@sweep-io/sweep-design';
import { EnvSelector } from './EnvSelector';
import { Close } from '@sweep-io/sweep-design/dist/icons/Close';
import { useSelector } from 'react-redux';
import { usedFunnelMapCrmOrgIds } from '../../configuration-canvas/utils/listFunnelMapCrmOrgIds';
import { useConfigurationCanvas } from '../../configuration-canvas/public/useConfigurationCanvas';
import { selectCrmOrgs } from '../environmentsReducer';
import { selectDefaultCreationCrmOrgId } from '../../../../reducers/userInfoReducer';
import { useChangeEnvironment } from '../../../../hooks/useChangeEnvironment';
import { useRunOnceWhenTruthy } from '../../../common/useRunOnceWhenTruthy';

export const panelEnvSelectorHack = {
  '& .panel-env-selector': {
    marginTop: -2,
  },
  '&.configuration-canvas-panel-expanded .panel-env-selector': {
    marginTop: -4,
  },
};

export const PanelEnvSelector = ({
  onClose,
  filterByEnvsUsedInCanvas,
}: {
  onClose?: () => void;
  filterByEnvsUsedInCanvas?: boolean;
}) => {
  const currentCrmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const { canvasFunnelMap } = useConfigurationCanvas();
  const allCrmOrgs = useSelector(selectCrmOrgs);
  const salesforceOrgs = allCrmOrgs.filter((crmOrg) => crmOrg.orgType === 'salesforce');
  let envsToShow = salesforceOrgs.map((crmOrg) => crmOrg.id);
  const { changeEnvironment } = useChangeEnvironment();

  if (filterByEnvsUsedInCanvas) {
    const usedCrmOrgIds = usedFunnelMapCrmOrgIds(canvasFunnelMap);
    envsToShow = envsToShow.filter((crmOrgId) => usedCrmOrgIds.includes(crmOrgId));
  }

  // If there is only one environment and it is the current one, we don't need to show the selector
  // But we need to change the current environment to the one that is being shown.
  //TODO SWEEP-8527
  useRunOnceWhenTruthy(
    () => {
      changeEnvironment(envsToShow[0]);
    },
    envsToShow.length === 1 && envsToShow[0] !== currentCrmOrgId,
  );

  if (envsToShow.length <= 1 && !onClose) {
    return null;
  }

  const onlyOneEnv = envsToShow.length === 1;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: onlyOneEnv ? 'flex-end' : 'space-between',
        backgroundColor: colors.grey[100],
        borderBottom: `1px solid ${colors.grey[300]}`,
        padding: '16px',
        height: '40px',
        gap: 1,
      }}
      className="panel-env-selector"
    >
      {!onlyOneEnv && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 0.5,
          }}
        >
          <Typography variant="body" color={colors.grey[700]}>
            Environment:
          </Typography>

          <EnvSelector
            variant="text"
            disableManageEnv
            menuProps={{
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
            }}
            allowedEnvIds={envsToShow}
          />
        </Box>
      )}

      {onClose && (
        <IconButton onClick={onClose} variant="flat" size="small">
          <Close />
        </IconButton>
      )}
    </Box>
  );
};
